import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MiPerfilTab } from '../../core/models';
import { RolPlataforma } from '../../core/models/novedades.models';
import { RoleContextService } from '../../core/services/role-context.service';
import { AccessibilityToolbar } from '../../shared/components/accessibility-toolbar/accessibility-toolbar';
import { BreadcrumbItem, Breadcrumbs } from '../../shared/components/breadcrumbs/breadcrumbs';
import { HeaderNotificationBell } from '../../shared/components/header-notification-bell/header-notification-bell';
import { UserSessionSummary } from '../../shared/components/user-session-summary/user-session-summary';
import { SidebarMenu } from '../../shared/sidebar-menu/sidebar-menu';
import { PerfilCurriculumTab } from './components/perfil-curriculum-tab/perfil-curriculum-tab';
import { PerfilDatosPersonalesTab } from './components/perfil-datos-personales-tab/perfil-datos-personales-tab';
import { PerfilPostulacionesTab } from './components/perfil-postulaciones-tab/perfil-postulaciones-tab';

@Component({
  selector: 'app-mi-perfil',
  imports: [
    CommonModule,
    FormsModule,
    SidebarMenu,
    PerfilPostulacionesTab,
    PerfilDatosPersonalesTab,
    PerfilCurriculumTab,
    HeaderNotificationBell,
    AccessibilityToolbar,
    Breadcrumbs,
    UserSessionSummary,
  ],
  templateUrl: './mi-perfil.html',
  styleUrl: './mi-perfil.css',
})
export class MiPerfil implements OnInit {
  readonly breadcrumbs: BreadcrumbItem[] = [{ label: 'Inicio', link: '/principal' }, { label: 'Mi perfil' }];
  private static readonly ROLES_CON_POSTULACIONES: RolPlataforma[] = [
    'postulante',
    'ayudante_postulante',
    'representante_institucional',
    'ayudante_representante',
  ];
  private static readonly ROLES_CON_CURRICULUM: RolPlataforma[] = ['postulante'];

  readonly tabsBase: Array<{ key: MiPerfilTab; label: string }> = [
    { key: 'postulaciones', label: 'Postulaciones' },
    { key: 'datos_personales', label: 'Datos Personales' },
    { key: 'curriculum', label: 'Curriculum' },
  ];
  rolSeleccionado: RolPlataforma = 'postulante';
  tabActiva: MiPerfilTab = 'datos_personales';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly roleContext: RoleContextService
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap.subscribe((params) => {
      this.rolSeleccionado = this.roleContext.resolveRole(params, this.rolSeleccionado);
      this.tabActiva = this.parseTabParam(params.get('tab'));
      this.ajustarTabActivaPorRol();
    });
  }

  get tabsDisponibles(): Array<{ key: MiPerfilTab; label: string }> {
    const tabsPorRol = this.permiteCurriculum
      ? this.tabsBase
      : this.tabsBase.filter((tab) => tab.key !== 'curriculum');

    if (this.mostrarTabPostulaciones) {
      if (this.esRolInstitucional) {
        return tabsPorRol.map((tab) =>
          tab.key === 'postulaciones' ? { ...tab, label: 'Postulaciones de Mi Institucion' } : tab
        );
      }
      return tabsPorRol.map((tab) => (tab.key === 'postulaciones' ? { ...tab, label: 'Mis postulaciones' } : tab));
    }

    return tabsPorRol.filter((tab) => tab.key !== 'postulaciones');
  }

  get mostrarTabPostulaciones(): boolean {
    return MiPerfil.ROLES_CON_POSTULACIONES.includes(this.rolSeleccionado);
  }

  get esRolInstitucional(): boolean {
    return this.rolSeleccionado === 'representante_institucional' || this.rolSeleccionado === 'ayudante_representante';
  }

  get descripcionPerfil(): string {
    if (this.tabActiva === 'postulaciones' && this.mostrarTabPostulaciones) {
      return 'Consulta y realiza el seguimiento de tus trámites en curso e históricos.';
    }

    if (this.tabActiva === 'curriculum' && this.permiteCurriculum) {
      return 'Mantén al día tus antecedentes de trayectoria. Esta información se sincronizará automáticamente con el formulario de cada una de tus propuestas en preparación.';
    }

    if (this.permiteCurriculum) {
      return 'Aqui puedes consultar el estado de tus postulaciones y actualizar tu CV e informacion personal.';
    }

    if (this.mostrarTabPostulaciones) {
      return 'Aqui puedes consultar las postulaciones asociadas a tu rol y actualizar tu informacion personal.';
    }

    return 'Aqui puedes actualizar tu informacion personal y revisar las opciones disponibles para tu rol.';
  }

  seleccionarTab(tab: MiPerfilTab): void {
    if (tab === 'postulaciones' && !this.mostrarTabPostulaciones) return;
    if (tab === 'curriculum' && !this.permiteCurriculum) return;
    this.tabActiva = tab;
  }

  private ajustarTabActivaPorRol(): void {
    if (!this.mostrarTabPostulaciones && this.tabActiva === 'postulaciones') {
      this.tabActiva = 'datos_personales';
    }
    if (!this.permiteCurriculum && this.tabActiva === 'curriculum') {
      this.tabActiva = 'datos_personales';
    }
    if (this.mostrarTabPostulaciones && !this.tabsDisponibles.some((tab) => tab.key === this.tabActiva)) {
      this.tabActiva = 'postulaciones';
    }
  }

  get permiteCurriculum(): boolean {
    return MiPerfil.ROLES_CON_CURRICULUM.includes(this.rolSeleccionado);
  }

  private parseTabParam(tab: string | null): MiPerfilTab {
    if (tab === 'postulaciones' || tab === 'datos_personales' || tab === 'curriculum') {
      return tab;
    }
    return this.tabActiva;
  }
}
